<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <!-- Stepper -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-body p-4">
            <div
              class="d-flex justify-content-between align-items-center position-relative"
            >
              <div
                class="position-absolute top-50 start-0 w-100 border-top z-0"
                style="border-color: #eee"
              ></div>
              <div
                v-for="step in 7"
                :key="step"
                class="z-10 bg-white px-2 d-flex flex-column align-items-center cursor-pointer"
                @click="goToStep(step)"
              >
                <div
                  class="rounded-circle d-flex align-items-center justify-content-center mb-2 transition-all"
                  :class="
                    currentStep >= step
                      ? 'bg-dark text-white shadow-lg'
                      : 'bg-light text-muted border'
                  "
                  style="
                    width: 32px;
                    height: 32px;
                    font-size: 14px;
                    font-weight: bold;
                  "
                >
                  {{ step }}
                </div>
                <span
                  class="text-xs font-weight-bold text-uppercase tracking-wider"
                  :class="currentStep >= step ? 'text-dark' : 'text-muted'"
                >
                  {{ getStepLabel(step) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <form @submit.prevent>
          <!-- Step 1: Branding -->
          <div v-if="currentStep === 1" class="fade-in">
            <div class="card border-0 shadow-sm">
              <div class="card-header bg-white pb-0 pt-4">
                <h5 class="font-serif">Select Brand</h5>
              </div>
              <div class="card-body">
                <div class="row g-4">
                  <div
                    class="col-6 col-md-3"
                    v-for="brand in brands"
                    :key="brand.id"
                  >
                    <div
                      class="card h-100 border transition-all cursor-pointer hover-card"
                      :class="
                        product.brand_id === brand.id
                          ? 'border-primary bg-light'
                          : 'border-light'
                      "
                      @click="selectBrand(brand.id)"
                    >
                      <div
                        class="card-body d-flex align-items-center justify-content-center p-4"
                      >
                        <span class="font-serif fw-bold">{{ brand.name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="text-end mt-5">
                  <button
                    class="btn btn-dark"
                    :disabled="!product.brand_id"
                    @click="nextStep"
                  >
                    Next Step <i class="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Category -->
          <div v-if="currentStep === 2" class="fade-in">
            <div class="card border-0 shadow-sm">
              <div class="card-header bg-white pb-0 pt-4">
                <h5 class="font-serif">Select Category</h5>
              </div>
              <div class="card-body">
                <div
                  v-if="!filteredCategories.length"
                  class="text-center py-5 text-muted"
                >
                  <p>No categories available for this brand.</p>
                  <small
                    >Please ensure the brand has categories assigned.</small
                  >
                </div>

                <div class="row g-4" v-else>
                  <!-- Iterate Roots as Blocks (Unwrapped if single root) -->
                  <div
                    class="col-md-6"
                    v-for="root in displayCategoryBlocks"
                    :key="root.id"
                  >
                    <div class="card h-100 border bg-light">
                      <div
                        class="card-header border-bottom bg-white d-flex align-items-center justify-content-between py-3"
                      >
                        <div class="form-check m-0">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="'root-' + root.id"
                            :checked="product.categories.includes(root.id)"
                            @change="toggleRoot(root, $event)"
                          />
                          <label
                            class="form-check-label font-weight-bold mb-0 text-dark"
                            :for="'root-' + root.id"
                          >
                            {{ root.name }}
                          </label>
                        </div>
                      </div>
                      <div
                        class="card-body custom-scrollbar"
                        style="max-height: 300px; overflow-y: auto"
                      >
                        <div v-if="root.children && root.children.length > 0">
                          <category-item
                            v-for="child in root.children"
                            :key="child.id"
                            :category="child"
                            :ancestors="[root.id]"
                            v-model="product.categories"
                          />
                        </div>
                        <div v-else class="text-muted text-xs p-2">
                          No sub-categories
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-content-between mt-5">
                  <button class="btn btn-outline-secondary" @click="prevStep">
                    Back
                  </button>
                  <button
                    class="btn btn-dark"
                    :disabled="product.categories.length === 0"
                    @click="nextStep"
                  >
                    Next Step <i class="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Product Configuration -->
          <div v-if="currentStep === 3" class="fade-in">
            <!-- Section 1: Basic Information -->
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-header bg-white pb-0 pt-4">
                <h5
                  class="font-serif text-uppercase tracking-wider text-xs font-bold text-muted"
                >
                  Section 1: Basic Info
                </h5>
              </div>
              <div class="card-body">
                <div class="row g-4">
                  <!-- Name -->
                  <div class="col-md-4">
                    <label
                      class="form-label text-xs text-uppercase font-weight-bold text-muted"
                      >Name <span class="text-danger">*</span></label
                    >
                    <input
                      v-model="product.name"
                      class="form-control premium-input"
                      placeholder="Product Name"
                    />
                  </div>
                  <!-- Code -->
                  <div class="col-md-4">
                    <label
                      class="form-label text-xs text-uppercase font-weight-bold text-muted"
                      >Code</label
                    >
                    <input
                      v-model="product.product_code"
                      class="form-control premium-input"
                      placeholder="Product Code"
                    />
                  </div>
                  <!-- Condition -->
                  <div class="col-md-4">
                    <label
                      class="form-label text-xs text-uppercase font-weight-bold text-muted"
                      >Condition <span class="text-danger">*</span></label
                    >
                    <select
                      v-model="product.condition"
                      class="form-select premium-input"
                    >
                      <option value="new">New (全新)</option>
                      <option value="used">Used (二手)</option>
                    </select>
                  </div>

                  <!-- Detailed Type (Product Category) -->
                  <div class="col-md-12">
                    <label
                      class="form-label text-xs text-uppercase font-weight-bold text-muted"
                      >Category (Select to auto-configure Attributes)</label
                    >
                    <select
                      v-model="product.product_type_detail"
                      class="form-select premium-input"
                      @change="onDetailedTypeChange($event.target.value)"
                    >
                      <option value="">Select Category...</option>
                      <option
                        v-for="(typeResult, key) in getDetailedTypes()"
                        :key="key"
                        :value="key"
                      >
                        {{ typeResult.label }}
                      </option>
                    </select>
                  </div>
                  <!-- Tags -->
                  <div class="col-md-12 mt-3">
                    <tag-selector v-model="product.tags" label="PRODUCT TAGS" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Section 2: SKU Attribute Configuration -->
            <div class="card border-0 shadow-sm mb-4">
              <div
                class="card-header bg-white pb-0 pt-4 d-flex justify-content-between align-items-center"
              >
                <h5
                  class="font-serif text-uppercase tracking-wider text-xs font-bold text-muted"
                >
                  Section 2: SKU Attributes
                </h5>
                <button
                  class="btn btn-sm btn-outline-dark"
                  @click="addOptionType"
                >
                  + Add Attribute
                </button>
              </div>
              <div class="card-body">
                <div
                  v-if="product.options.length === 0"
                  class="text-center text-muted py-4 bg-light rounded border border-dashed"
                >
                  <p class="mb-2">No attributes defined.</p>
                  <p class="text-xs">
                    Click "Load Defaults" next to Type or "Add Attribute" above.
                  </p>
                </div>
                <div v-else>
                  <div
                    v-for="(opt, i) in product.options"
                    :key="i"
                    class="mb-4 pb-3 border-bottom last:border-0"
                  >
                    <!-- Option Name & Controls -->
                    <div class="d-flex align-items-center mb-3">
                      <div
                        class="input-group input-group-sm"
                        style="width: 200px"
                      >
                        <span class="input-group-text bg-light border-end-0"
                          ><i class="fas fa-tag text-xs"></i
                        ></span>
                        <input
                          v-model="opt.name"
                          class="form-control fw-bold border-start-0 ps-0"
                          placeholder="Attribute Name (e.g. Color)"
                        />
                      </div>
                      <button
                        class="btn btn-link text-danger p-0 ms-3"
                        @click="removeOptionType(i)"
                        title="Remove Attribute"
                      >
                        <i class="fas fa-trash text-sm"></i>
                      </button>
                    </div>

                    <!-- Option Values -->
                    <div
                      class="d-flex flex-wrap gap-2 align-items-center ms-0 ms-md-4"
                    >
                      <span
                        v-for="(val, vIdx) in opt.values"
                        :key="vIdx"
                        class="badge bg-white text-dark border px-3 py-2 rounded-pill d-flex align-items-center shadow-sm"
                      >
                        {{ val }}
                        <i
                          class="fas fa-times ms-2 cursor-pointer text-muted hover:text-danger"
                          @click="removeOptionValue(i, vIdx)"
                        ></i>
                      </span>
                      <!-- Add Value Input -->
                      <div
                        class="d-flex align-items-center"
                        style="width: 150px"
                      >
                        <input
                          type="text"
                          class="form-control form-control-sm rounded-pill"
                          placeholder="+ Add Value"
                          @keydown.enter.prevent="addOptionValue(i, $event)"
                          @blur="addOptionValue(i, $event)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section 3: SKU List -->
            <div class="card border-0 shadow-sm">
              <div
                class="card-header bg-white pb-0 pt-4 d-flex justify-content-between align-items-center"
              >
                <h5
                  class="font-serif text-uppercase tracking-wider text-xs font-bold text-muted"
                >
                  Section 3: SKU Settings
                </h5>
                <button
                  class="btn btn-dark btn-sm shadow-sm"
                  @click="openOptionModal()"
                >
                  <i class="fas fa-plus me-1"></i> New SKU
                </button>
              </div>
              <div class="card-body">
                <div
                  v-if="variants.length === 0"
                  class="text-center text-muted py-5"
                >
                  No SKUs created yet. Click "New SKU" to add one.
                </div>
                <div v-else class="table-responsive">
                  <table class="table align-items-center table-hover">
                    <thead class="thead-light">
                      <tr>
                        <th
                          class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                        >
                          SKU Information
                        </th>
                        <!-- Dynamic Headers based on Options -->
                        <th
                          v-for="opt in product.options"
                          :key="opt.name"
                          class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                        >
                          {{ opt.name }}
                        </th>
                        <th
                          class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                        >
                          Price / Sale
                        </th>
                        <th
                          class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                        >
                          Stock
                        </th>
                        <th class="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(v, idx) in variants" :key="idx">
                        <td>
                          <div class="d-flex px-2 py-1">
                            <div
                              class="d-flex flex-column justify-content-center"
                            >
                              <h6 class="mb-0 text-xs">
                                {{ v.variant_name || "No Name" }}
                              </h6>
                              <p class="text-xs text-secondary mb-0">
                                {{ v.variant_code }}
                              </p>
                            </div>
                          </div>
                        </td>
                        <!-- Dynamic Values -->
                        <td v-for="opt in product.options" :key="opt.name">
                          <span
                            class="text-secondary text-xs font-weight-bold text-dark"
                          >
                            {{
                              v.option_values ? v.option_values[opt.name] : "-"
                            }}
                          </span>
                        </td>
                        <!-- Price -->
                        <td>
                          <div class="d-flex flex-column">
                            <span class="text-dark text-xs font-weight-bold"
                              >NT$ {{ v.sale_price || v.price }}</span
                            >
                            <span
                              v-if="v.sale_price && v.price > v.sale_price"
                              class="text-secondary text-xxs line-through"
                              >NT$ {{ v.price }}</span
                            >
                          </div>
                        </td>
                        <!-- Stock -->
                        <td>
                          <span
                            class="text-secondary text-xs font-weight-bold"
                            >{{ v.stock }}</span
                          >
                        </td>
                        <td class="align-middle text-end">
                          <button
                            class="btn btn-link text-secondary mb-0 px-2"
                            @click="openOptionModal(v, idx)"
                          >
                            <i class="fas fa-edit text-xs"></i>
                          </button>
                          <button
                            class="btn btn-link text-danger mb-0 px-2"
                            @click="removeVariant(idx)"
                          >
                            <i class="fas fa-trash text-xs"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div
                  class="d-flex justify-content-between mt-5 pt-3 border-top"
                >
                  <!-- Standard Nav Buttons -->
                  <button class="btn btn-outline-secondary" @click="prevStep">
                    Back
                  </button>
                  <button class="btn btn-dark" @click="nextStep">
                    Next: Gallery <i class="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4: Media -->
          <div v-if="currentStep === 4" class="fade-in">
            <div class="card border-0 shadow-sm">
              <div class="card-body">
                <h5 class="font-serif mb-4">Cover Image Settings</h5>
                <p class="text-sm text-muted mb-4">
                  Configure specific cover images for different device views.
                </p>

                <div class="card bg-light border-0 mb-4">
                  <div class="card-body">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fas fa-desktop me-2"></i>Desktop Layout (4:3
                      Aspect Ratio)
                    </h6>
                    <div class="row">
                      <div class="col-md-6">
                        <label class="form-label text-xs uppercase text-muted"
                          >List Cover Image</label
                        >
                        <media-uploader
                          :model-value="
                            product.details.list_cover_image
                              ? [product.details.list_cover_image]
                              : []
                          "
                          @update:model-value="
                            (files) =>
                              (product.details.list_cover_image =
                                files[0] || '')
                          "
                          :max-files="1"
                          :return-objects="true"
                          :aspect-ratio="1.33"
                          label="Desktop Cover"
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label text-xs uppercase text-muted"
                          >List Sub-Cover Image (Hover)</label
                        >
                        <media-uploader
                          :model-value="
                            product.details.list_hover_image
                              ? [product.details.list_hover_image]
                              : []
                          "
                          @update:model-value="
                            (files) =>
                              (product.details.list_hover_image =
                                files[0] || '')
                          "
                          :max-files="1"
                          :return-objects="true"
                          :aspect-ratio="1.33"
                          label="Desktop Hover"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card bg-light border-0 mb-5">
                  <div class="card-body">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fas fa-mobile-alt me-2"></i>Mobile Layout (3:4
                      Aspect Ratio)
                    </h6>
                    <div class="row">
                      <div class="col-md-6">
                        <label class="form-label text-xs uppercase text-muted"
                          >Mobile List Cover</label
                        >
                        <p class="text-xs text-muted mb-2">
                          Displayed on mobile devices (Vertical View).
                        </p>
                        <media-uploader
                          :model-value="
                            product.details.list_mobile_cover_image
                              ? [product.details.list_mobile_cover_image]
                              : []
                          "
                          @update:model-value="
                            (files) =>
                              (product.details.list_mobile_cover_image =
                                files[0] || '')
                          "
                          :max-files="1"
                          :return-objects="true"
                          :aspect-ratio="0.75"
                          label="Mobile Cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <hr class="my-4" />

                <h5 class="font-serif mb-4">Product Page Gallery (Min 3:4)</h5>
                <media-uploader
                  v-model="product.media_urls"
                  :max-files="12"
                  :return-objects="true"
                  :aspect-ratio="0.75"
                />
                <div class="d-flex justify-content-between mt-5">
                  <button class="btn btn-outline-secondary" @click="prevStep">
                    Back
                  </button>
                  <button class="btn btn-dark" @click="nextStep">
                    Next Step <i class="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 5: Details (Premium Content) -->
          <div v-if="currentStep === 5" class="fade-in">
            <div class="card border-0 shadow-sm">
              <div class="card-header bg-white pb-0 pt-4">
                <ul class="nav nav-tabs border-bottom-0">
                  <li class="nav-item">
                    <a
                      class="nav-link cursor-pointer"
                      :class="{
                        active: activeTab === 'desc',
                        'fw-bold border-bottom border-dark':
                          activeTab === 'desc',
                      }"
                      @click="activeTab = 'desc'"
                      >Description</a
                    >
                  </li>
                  <li class="nav-item">
                    <a
                      class="nav-link cursor-pointer"
                      :class="{
                        active: activeTab === 'specs',
                        'fw-bold border-bottom border-dark':
                          activeTab === 'specs',
                      }"
                      @click="activeTab = 'specs'"
                      >Specs</a
                    >
                  </li>
                  <li class="nav-item">
                    <a
                      class="nav-link cursor-pointer"
                      :class="{
                        active: activeTab === 'marketing',
                        'fw-bold border-bottom border-dark':
                          activeTab === 'marketing',
                      }"
                      @click="activeTab = 'marketing'"
                      >Marketing</a
                    >
                  </li>
                  <li class="nav-item">
                    <a
                      class="nav-link cursor-pointer"
                      :class="{
                        active: activeTab === 'delivery',
                        'fw-bold border-bottom border-dark':
                          activeTab === 'delivery',
                      }"
                      @click="activeTab = 'delivery'"
                      >Delivery</a
                    >
                  </li>
                  <li class="nav-item">
                    <a
                      class="nav-link cursor-pointer"
                      :class="{
                        active: activeTab === 'size_chart',
                        'fw-bold border-bottom border-dark':
                          activeTab === 'size_chart',
                      }"
                      @click="activeTab = 'size_chart'"
                      >Size Chart</a
                    >
                  </li>
                </ul>
              </div>
              <div class="card-body">
                <!-- Description Tab -->
                <div v-show="activeTab === 'desc'">
                  <rich-text-editor v-model="product.description" />
                </div>

                <!-- Specs Tab -->
                <div v-show="activeTab === 'specs'">
                  <div
                    v-for="(feat, i) in product.details.features"
                    :key="i"
                    class="row mb-2 align-items-center"
                  >
                    <div class="col-4">
                      <input
                        v-model="feat.title"
                        class="form-control form-control-sm"
                        placeholder="Title"
                      />
                    </div>
                    <div class="col-7">
                      <input
                        v-model="feat.content"
                        class="form-control form-control-sm"
                        placeholder="Content"
                      />
                    </div>
                    <div class="col-1">
                      <i
                        class="fas fa-times text-danger cursor-pointer"
                        @click="product.details.features.splice(i, 1)"
                      ></i>
                    </div>
                  </div>
                  <button
                    class="btn btn-sm btn-link text-dark ps-0"
                    @click.prevent="
                      product.details.features.push({ title: '', content: '' })
                    "
                  >
                    + Add Spec
                  </button>
                </div>

                <!-- Delivery Tab -->
                <div v-show="activeTab === 'delivery'">
                  <rich-text-editor v-model="product.delivery" />
                </div>

                <!-- Size Chart Tab -->
                <div v-show="activeTab === 'size_chart'">
                  <label class="form-label text-xs text-muted"
                    >Size Chart Image</label
                  >
                  <media-uploader
                    :model-value="
                      product.size_chart_image ? [product.size_chart_image] : []
                    "
                    @update:model-value="
                      (files) => (product.size_chart_image = files[0] || '')
                    "
                    :max-files="1"
                    :allow-videos="false"
                  />
                  <div class="mt-3">
                    <label class="form-label text-xs text-muted"
                      >Size Chart Label (Button Text)</label
                    >
                    <input
                      type="text"
                      class="form-control"
                      v-model="product.size_chart_label"
                      placeholder="Default: 尺寸表"
                    />
                  </div>
                </div>

                <!-- SEO Tab -->
                <div v-show="activeTab === 'seo'">
                  <div class="mb-3">
                    <label class="form-label text-xs text-muted"
                      >SEO Title (OG Title)</label
                    >
                    <input
                      v-model="product.og_title"
                      class="form-control"
                      placeholder="Leave empty to use Product Name"
                    />
                  </div>
                  <div class="mb-3">
                    <label class="form-label text-xs text-muted"
                      >SEO Description (OG Description)</label
                    >
                    <textarea
                      v-model="product.og_description"
                      class="form-control"
                      rows="3"
                      placeholder="Leave empty to use Product Description"
                    ></textarea>
                  </div>
                  <div class="mb-3">
                    <label class="form-label text-xs text-muted"
                      >SEO Image (OG Image) - Replaces default image</label
                    >
                    <media-uploader
                      :model-value="product.og_image ? [product.og_image] : []"
                      @update:model-value="
                        (files) => (product.og_image = files[0] || '')
                      "
                      :max-files="1"
                      :allow-videos="false"
                    />
                  </div>
                </div>

                <!-- Marketing Tab -->
                <div v-show="activeTab === 'marketing'">
                  <div
                    class="d-flex justify-content-between align-items-center mb-3"
                  >
                    <div
                      class="alert bg-light border text-xs mb-0 flex-grow-1 me-3"
                    >
                      Configure the "3+1" marketing layout: 3 Feature Icons and
                      1 Highlight Banner.
                    </div>
                    <div class="d-flex gap-2">
                      <button
                        class="btn btn-sm btn-outline-dark"
                        @click="openLoadTemplateModal"
                      >
                        <i class="fas fa-download me-1"></i> Load Template
                      </button>
                      <button
                        class="btn btn-sm btn-dark"
                        @click="openSaveTemplateModal"
                      >
                        <i class="fas fa-save me-1"></i> Save Template
                      </button>
                    </div>
                  </div>

                  <!-- Feature Styling Controls -->
                  <div class="bg-light p-3 rounded mb-4">
                    <h6 class="text-xs text-uppercase font-weight-bold mb-3">
                      Feature Styling
                    </h6>
                    <div class="row g-3">
                      <div class="col-md-4">
                        <label class="form-label text-xs text-muted"
                          >Icon Size</label
                        >
                        <select
                          v-model="
                            product.details.marketing_features_style.icon_size
                          "
                          class="form-select form-select-sm"
                        >
                          <option value="text-5xl">Standard (5xl)</option>
                          <option value="text-6xl">Large (6xl)</option>
                          <option value="text-7xl">Extra Large (7xl)</option>
                          <option value="text-8xl">Huge (8xl)</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label text-xs text-muted"
                          >Title Size</label
                        >
                        <select
                          v-model="
                            product.details.marketing_features_style.title_size
                          "
                          class="form-select form-select-sm"
                        >
                          <option value="text-base">Small (base)</option>
                          <option value="text-lg">Medium (lg)</option>
                          <option value="text-xl">Large (xl)</option>
                          <option value="text-2xl">Extra Large (2xl)</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label text-xs text-muted"
                          >Title Color</label
                        >
                        <div class="d-flex gap-2 align-items-center">
                          <input
                            type="color"
                            class="form-control form-control-color"
                            v-model="
                              product.details.marketing_features_style
                                .title_color
                            "
                            style="width: 40px"
                          />
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="
                              product.details.marketing_features_style
                                .title_color
                            "
                            placeholder="#000000"
                          />
                        </div>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label text-xs text-muted"
                          >Subtitle Size</label
                        >
                        <select
                          v-model="
                            product.details.marketing_features_style
                              .subtitle_size
                          "
                          class="form-select form-select-sm"
                        >
                          <option value="text-xs">Extra Small (xs)</option>
                          <option value="text-sm">Small (sm)</option>
                          <option value="text-base">Medium (base)</option>
                          <option value="text-lg">Large (lg)</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label text-xs text-muted"
                          >Subtitle Color</label
                        >
                        <div class="d-flex gap-2 align-items-center">
                          <input
                            type="color"
                            class="form-control form-control-color"
                            v-model="
                              product.details.marketing_features_style
                                .subtitle_color
                            "
                            style="width: 40px"
                          />
                          <input
                            type="text"
                            class="form-control form-control-sm"
                            v-model="
                              product.details.marketing_features_style
                                .subtitle_color
                            "
                            placeholder="#666666"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 3 Features -->
                  <h6 class="text-xs text-uppercase font-weight-bold mb-3">
                    Top 3 Features
                  </h6>
                  <div class="row g-3 mb-4">
                    <div
                      class="col-md-4"
                      v-for="(item, idx) in product.details.marketing_features"
                      :key="idx"
                    >
                      <div class="p-3 border rounded bg-white h-100">
                        <!-- Icon Selector or Search -->
                        <div class="mb-2">
                          <label
                            class="form-label text-xxs text-uppercase text-muted"
                            >Icon (Class or Image)</label
                          >
                          <div class="d-flex align-items-center gap-2 mb-2">
                            <icon-selector
                              v-if="!item.icon || !item.icon.includes('/')"
                              v-model="item.icon"
                            />
                            <div
                              v-else
                              class="preview-icon-img"
                              style="width: 40px; height: 40px"
                            >
                              <img
                                :src="item.icon"
                                class="w-100 h-100 object-contain"
                              />
                            </div>
                            <button
                              v-if="item.icon"
                              class="btn btn-xs btn-outline-danger mb-0"
                              @click="item.icon = ''"
                            >
                              Clear
                            </button>
                          </div>

                          <!-- Upload Alternative -->
                          <media-uploader
                            v-if="!item.icon || item.icon.includes('/')"
                            :model-value="
                              item.icon && item.icon.includes('/')
                                ? [item.icon]
                                : []
                            "
                            @update:model-value="
                              (files) => (item.icon = files[0] || '')
                            "
                            :max-files="1"
                            :allow-videos="false"
                            label="Upload Custom Icon"
                          />
                        </div>

                        <input
                          v-model="item.title"
                          class="form-control form-control-sm mb-2"
                          placeholder="Title"
                        />
                        <textarea
                          v-model="item.content"
                          class="form-control form-control-sm"
                          rows="2"
                          placeholder="Short description..."
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <!-- Banner -->
                  <h6 class="text-xs text-uppercase font-weight-bold mb-3">
                    Highlight Banner
                  </h6>
                  <div class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label text-xs text-muted"
                        >Banner Media (Image or Video)</label
                      >
                      <!-- Use MediaUploader in single file mode -->
                      <media-uploader
                        :model-value="
                          product.details.marketing_banner.image
                            ? [product.details.marketing_banner.image]
                            : []
                        "
                        @update:model-value="
                          (files) =>
                            (product.details.marketing_banner.image =
                              files[0] || '')
                        "
                        :max-files="1"
                        :allow-videos="true"
                      />
                    </div>
                    <div class="col-md-8">
                      <input
                        v-model="product.details.marketing_banner.title"
                        class="form-control form-control-sm mb-2"
                        placeholder="Main Title"
                      />
                      <input
                        v-model="product.details.marketing_banner.subtitle"
                        class="form-control form-control-sm mb-2"
                        placeholder="Subtitle"
                      />
                      <textarea
                        v-model="product.details.marketing_banner.content"
                        class="form-control form-control-sm"
                        rows="4"
                        placeholder="Banner Content..."
                      ></textarea>
                      <div class="row g-2 mt-2">
                        <div class="col-md-6">
                          <label class="form-label text-xs text-muted"
                            >Background Color</label
                          >
                          <div class="d-flex gap-2 align-items-center">
                            <input
                              type="color"
                              class="form-control form-control-color"
                              v-model="
                                product.details.marketing_banner.bg_color
                              "
                              title="Choose color"
                              style="width: 40px"
                            />
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              v-model="
                                product.details.marketing_banner.bg_color
                              "
                              placeholder="#ffffff"
                            />
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="d-flex gap-2 align-items-center">
                            <input
                              type="color"
                              class="form-control form-control-color"
                              v-model="
                                product.details.marketing_banner.text_color
                              "
                              title="Choose color"
                              style="width: 40px"
                            />
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              v-model="
                                product.details.marketing_banner.text_color
                              "
                              placeholder="#000000"
                            />
                          </div>
                        </div>
                        <!-- New Color Pickers for Title & Subtitle -->
                        <div class="col-md-6 mt-2">
                          <label class="form-label text-xs text-muted"
                            >Title Color</label
                          >
                          <div class="d-flex gap-2 align-items-center">
                            <input
                              type="color"
                              class="form-control form-control-color"
                              v-model="
                                product.details.marketing_banner.title_color
                              "
                              title="Choose color"
                              style="width: 40px"
                            />
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              v-model="
                                product.details.marketing_banner.title_color
                              "
                              placeholder="#000000"
                            />
                          </div>
                        </div>
                        <div class="col-md-6 mt-2">
                          <label class="form-label text-xs text-muted"
                            >Subtitle Color</label
                          >
                          <div class="d-flex gap-2 align-items-center">
                            <input
                              type="color"
                              class="form-control form-control-color"
                              v-model="
                                product.details.marketing_banner.subtitle_color
                              "
                              title="Choose color"
                              style="width: 40px"
                            />
                            <input
                              type="text"
                              class="form-control form-control-sm"
                              v-model="
                                product.details.marketing_banner.subtitle_color
                              "
                              placeholder="#000000"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-content-between mt-5">
                  <button class="btn btn-outline-secondary" @click="prevStep">
                    Back
                  </button>
                  <button
                    class="btn btn-dark px-5"
                    @click="saveProduct(true)"
                    :disabled="isLoading"
                  >
                    <i class="fas fa-check me-2"></i> Save & Publish
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 6: Recommendations -->
          <div v-if="currentStep === 6" class="fade-in">
            <div class="card border-0 shadow-sm">
              <div class="card-header bg-white pb-0 pt-4">
                <h5 class="font-serif">Upsell / Complete the Look</h5>
                <p class="text-xs text-muted">
                  Configure products shown in the "Complete the Look" section.
                </p>
              </div>
              <div class="card-body">
                <div class="mb-4">
                  <label class="form-label font-weight-bold"
                    >Recommendation Type</label
                  >
                  <div class="d-flex gap-3">
                    <div class="form-check card-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rec_type"
                        id="rec_cat"
                        value="category"
                        v-model="product.recommendation_type"
                      />
                      <label class="form-check-label" for="rec_cat"
                        >Same Category</label
                      >
                    </div>
                    <div class="form-check card-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rec_type"
                        id="rec_best"
                        value="best_sellers"
                        v-model="product.recommendation_type"
                      />
                      <label class="form-check-label" for="rec_best"
                        >Best Sellers</label
                      >
                    </div>
                    <div class="form-check card-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rec_type"
                        id="rec_brand"
                        value="brand"
                        v-model="product.recommendation_type"
                      />
                      <label class="form-check-label" for="rec_brand"
                        >Same Brand</label
                      >
                    </div>
                    <div class="form-check card-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rec_type"
                        id="rec_new"
                        value="new_arrivals"
                        v-model="product.recommendation_type"
                      />
                      <label class="form-check-label" for="rec_new"
                        >New Arrivals</label
                      >
                    </div>
                    <div class="form-check card-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rec_type"
                        id="rec_price"
                        value="similar_price"
                        v-model="product.recommendation_type"
                      />
                      <label class="form-check-label" for="rec_price"
                        >Similar Price</label
                      >
                    </div>
                    <div class="form-check card-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rec_type"
                        id="rec_tag"
                        value="same_tag"
                        v-model="product.recommendation_type"
                      />
                      <label class="form-check-label" for="rec_tag"
                        >Same Tag</label
                      >
                    </div>
                    <div class="form-check card-radio">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="rec_type"
                        id="rec_custom"
                        value="custom"
                        v-model="product.recommendation_type"
                      />
                      <label class="form-check-label" for="rec_custom"
                        >Custom Selection</label
                      >
                    </div>
                  </div>
                </div>

                <!-- Custom Picker -->
                <div
                  v-if="product.recommendation_type === 'custom'"
                  class="mb-4 fade-in"
                >
                  <label class="form-label font-weight-bold"
                    >Select Products</label
                  >
                  <div class="border rounded p-3 bg-light">
                    <product-selector v-model="product.recommendation_ids" />
                  </div>
                </div>

                <div class="d-flex justify-content-between mt-5">
                  <button class="btn btn-outline-secondary" @click="prevStep">
                    Back
                  </button>
                  <button class="btn btn-dark px-5" @click="nextStep">
                    Next Step <i class="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>

        <!-- Variant Modal -->
        <product-variant-modal
          :show="showVariantModal"
          :product-type="product.product_type"
          :product-options="product.options"
          :variant-data="currentVariantData"
          @close="showVariantModal = false"
          @save="handleVariantSave"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CategoryItem from "@/components/CategoryItem.vue";
import IconSelector from "@/components/IconSelector.vue";
import MediaUploader from "@/components/MediaUploader.vue";
import ProductSelector from "@/components/ProductSelector.vue";
import ProductVariantModal from "@/components/ProductVariantModal.vue";
import RichTextEditor from "@/components/RichTextEditor.vue";
import TagSelector from "@/components/TagSelector.vue";
import { brandProductOptions } from "@/config/productOptions";
import request from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "ProductForm",
  components: {
    CategoryItem,
    MediaUploader,
    RichTextEditor,
    ProductVariantModal,
    IconSelector,
    ProductSelector,
    TagSelector,
  },
  data() {
    return {
      currentStep: 1,
      activeTab: "desc",
      isLoading: false,
      // Product Data
      products: [],
      // Product Data
      discountAmount: 0,
      product: {
        brand_id: null,
        categories: [],
        name: "",
        sku: "",
        price: "",
        sale_price: "",
        stock: 0,
        product_type: "",
        condition: "new", // Default condition
        tags: [],
        options: [],
        media_urls: [],
        description: "",
        details: {
          features: [],
          marketing_features: [
            { icon: "", title: "", content: "" },
            { icon: "", title: "", content: "" },
            { icon: "", title: "", content: "" },
          ],
          marketing_features_style: {
            icon_size: "text-6xl",
            title_size: "text-lg",
            title_color: "#000000",
            subtitle_size: "text-sm",
            subtitle_color: "#666666",
          },
          marketing_feature_title_color: "", // Global feature title color if needed
          marketing_banner: {
            image: "",
            title: "",
            subtitle: "",
            content: "",
            bg_color: "",
            text_color: "",
            title_color: "",
            subtitle_color: "",
          },
          delivery: "",
        },
        recommendation_type: "custom",
        recommendation_ids: [],
        size_chart_image: "",
        size_chart_label: "",
      },
      variants: [],
      brands: [],
      categories: [],
      showVariantModal: false,
      editingVariantIndex: null,
      currentVariantData: null,
      searchQuery: "",
      searchResults: [],

      cachedProducts: {}, // Cache for product names
      selectedRecCategory: null,
    };
  },
  computed: {
    productId() {
      return this.$route.params.id;
    },
    isEdit() {
      return !!this.productId;
    },
    filteredCategories() {
      if (!this.product.brand_id) return [];
      // Filter categories by brand if applicable
      return this.categories.filter(
        (c) => !c.brand_id || c.brand_id == this.product.brand_id
      );
    },
    displayCategoryBlocks() {
      return this.filteredCategories;
    },
    discountPercentage() {
      if (!this.product.price || !this.product.sale_price) return 0;
      if (parseFloat(this.product.sale_price) >= parseFloat(this.product.price))
        return 0;
      return Math.round(
        ((this.product.price - this.product.sale_price) / this.product.price) *
          100
      );
    },

    hasDynamicSpecs() {
      return this.product.options && this.product.options.length > 0;
    },
    flattenedCategories() {
      // Simple recursive flatten
      const flatten = (cats, prefix = "") => {
        let result = [];
        cats.forEach((c) => {
          result.push({ ...c, name: prefix + c.name });
          if (c.children && c.children.length) {
            result = result.concat(flatten(c.children, prefix + "- "));
          }
        });
        return result;
      };
      return flatten(this.categories);
    },
  },
  methods: {
    getStepLabel(s) {
      return [
        "Brand",
        "Category",
        "Info",
        "Gallery",
        "Details",
        "Upsell",
        "SEO",
      ][s - 1];
    },

    // ... other methods ...

    async searchProducts() {
      // Allow search if category selected OR query length >= 2
      if (
        !this.selectedRecCategory &&
        (!this.searchQuery || this.searchQuery.length < 2)
      ) {
        this.searchResults = [];
        return;
      }
      try {
        const params = {
          search: this.searchQuery || undefined,
        };
        if (this.selectedRecCategory) {
          params.category_id = this.selectedRecCategory;
        }

        const res = await request.get("/api/shop/products", {
          params,
        });
        this.searchResults = res.data.data.filter(
          (p) => p.id !== this.product.id
        );

        // Cache names
        this.searchResults.forEach((p) => {
          this.cachedProducts[p.id] = p.name;
        });
      } catch (e) {
        console.error(e);
      }
    },
    addRecommendation(p) {
      if (!this.product.recommendation_ids.includes(p.id)) {
        this.product.recommendation_ids.push(p.id);
      }
      this.searchQuery = "";
      this.searchResults = [];
    },
    removeRecommendation(idx) {
      this.product.recommendation_ids.splice(idx, 1);
    },
    getProductName(id) {
      return this.cachedProducts[id] || `Product #${id}`;
    },

    // ... existing methods
    goToStep(s) {
      if (this.currentStep < s && !this.validateStep(this.currentStep)) return;
      this.currentStep = s;
    },
    nextStep() {
      if (this.validateStep(this.currentStep)) {
        this.currentStep++;
      }
    },
    prevStep() {
      this.currentStep--;
    },
    validateStep(step) {
      if (step === 1 && !this.product.brand_id) {
        Swal.fire("Error", "Please select a brand", "error");
        return false;
      }
      if (step === 2 && this.product.categories.length === 0) {
        Swal.fire("Error", "Please select at least one category", "error");
        return false;
      }
      if (step === 3) {
        if (!this.product.name) {
          Swal.fire("Error", "Name is required", "error");
          return false;
        }
        if (!this.product.condition) {
          Swal.fire("Error", "Please select a condition", "error");
          return false;
        }
        if (this.variants.length === 0) {
          Swal.fire("Error", "Please create at least one SKU", "error");
          return false;
        }
      }
      return true;
    },
    async fetchBrands() {
      try {
        const res = await request.get("/api/admin/brands");
        this.brands = res.data.data;
      } catch (e) {
        console.error("Failed to fetch brands", e);
      }
    },
    async fetchCategories() {
      try {
        // Fetch Tree structure
        const res = await request.get("/api/admin/categories?tree=1");
        this.categories = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchProduct() {
      if (!this.isEdit) return;
      try {
        const res = await request.get(`/api/admin/products/${this.productId}`);
        const data = res.data.data;
        this.product = {
          ...this.product,
          ...data,
          tags: data.tags ? data.tags.map((t) => t.id) : [],
          details: {
            features: data.details?.features || [],
            marketing_features: data.details?.marketing_features || [
              { icon: "", title: "", content: "" },
              { icon: "", title: "", content: "" },
              { icon: "", title: "", content: "" },
            ],
            marketing_features_style: data.details
              ?.marketing_features_style || {
              icon_size: "text-6xl",
              title_size: "text-lg",
              title_color: "#000000",
              subtitle_size: "text-sm",
              subtitle_color: "#666666",
            },
            marketing_banner: data.details?.marketing_banner || {
              image: "",
              title: "",
              subtitle: "",
              content: "",
              bg_color: "",
              text_color: "",
              title_color: "",
              subtitle_color: "",
            },
            delivery: data.details?.delivery || "",
            // List Images (Hydrate or default)
            list_cover_image: data.details?.list_cover_image || "",
            list_hover_image: data.details?.list_hover_image || "",
            list_mobile_cover_image:
              data.details?.list_mobile_cover_image || "",
          },
          // Ensure arrays
          categories: data.categories.map((c) => c.id),
          brand_id: data.brand_id,
          recommendation_ids: data.recommendation_ids || [],
          recommendation_type: data.recommendation_type || "category",
          size_chart_image: data.size_chart_image || "",
          og_title: data.og_title || "",
          og_description: data.og_description || "",
          og_image: data.og_image || "",
        };
        // Init discount amount
        if (this.product.price && this.product.sale_price) {
          this.discountAmount = this.product.price - this.product.sale_price;
        }

        // Load Variants
        this.variants = (data.variants || []).map((v) => {
          // Hydrate option_values if missing
          if (!v.option_values || Array.isArray(v.option_values)) {
            v.option_values = {};
          }

          // Reverse Hydrate from known fields if needed (same logic as modal)
          if (this.product.options) {
            this.product.options.forEach((opt) => {
              const key = opt.name;
              // Only hydrate if empty/missing
              if (!v.option_values[key]) {
                const lowerKey = key.toLowerCase();
                if (lowerKey === "size" && v.size)
                  v.option_values[key] = v.size;
                else if (lowerKey === "color" && v.color)
                  v.option_values[key] = v.color;
                else if (lowerKey === "hand" && v.hand)
                  v.option_values[key] = v.hand;
                else if (
                  (lowerKey === "loft" || lowerKey === "loft angle") &&
                  v.loft_angle
                )
                  v.option_values[key] = v.loft_angle;
                else if (lowerKey === "shaft" && v.shaft_brand)
                  v.option_values[key] = v.shaft_brand;
                else if (lowerKey === "flex" && v.shaft_flex)
                  v.option_values[key] = v.shaft_flex;
              }
            });
          }
          return v;
        });
      } catch (e) {
        console.error(e);
      }
    },
    selectBrand(id) {
      this.product.brand_id = id;
    },
    openOptionModal(variant = null, index = null) {
      this.editingVariantIndex = index;
      this.currentVariantData = variant
        ? JSON.parse(JSON.stringify(variant))
        : null;
      this.showVariantModal = true;
    },
    handleVariantSave(variantData) {
      const { close, ...data } = variantData;
      if (this.editingVariantIndex !== null) {
        // Update existing
        this.variants.splice(this.editingVariantIndex, 1, data);
      } else {
        // Add new
        this.variants.push(data);
      }

      if (close) {
        this.showVariantModal = false;
        this.editingVariantIndex = null;
        this.currentVariantData = null;
      } else {
        // If "Save and Add Another", we might want to keep modal open but clear edit index if we were editing?
        // Usually "Save and Add Another" is for creating new.
        // If editing, it probably acts as "Save" then "Reset for New".
        this.editingVariantIndex = null;
        this.currentVariantData = null;
      }
      Swal.fire("Success", "Variant saved", "success");
    },
    async saveProduct(publish = false) {
      this.isLoading = true;
      try {
        const payload = {
          ...this.product,
          status: publish ? "active" : "draft",
          // Generic price/stock removed from UI, set defaults or derive from variants if needed
          // For now, setting dummy values if backend requires them, or 0.
          // In a real scenario, base price might be min(variant prices)
          price:
            this.variants.length > 0
              ? Math.min(...this.variants.map((v) => v.price))
              : 0,
          stock: this.variants.reduce(
            (sum, v) => sum + parseInt(v.stock || 0),
            0
          ),
          variants: this.variants,
        };

        let url = this.isEdit
          ? `/api/admin/products/${this.productId}`
          : "/api/admin/products";
        let method = this.isEdit ? "put" : "post";

        await request[method](url, payload);
        Swal.fire("Success", "Product saved successfully!", "success");
        this.$router.push("/products");
      } catch (e) {
        console.error(e);
        let msg = "Failed to save product";
        let isCodeError = false;

        if (e.response && e.response.status === 422 && e.response.data.errors) {
          // Check for specific field errors
          if (e.response.data.errors.product_code) {
            msg = `Product Code Conflict: The code "${this.product.product_code}" is already taken.`; // Keep internal logic identifier
            isCodeError = true;
          } else {
            // Translate common errors
            const errorMap = {
              "The name field is required.": "請輸入商品名稱。",
              "The price field is required.": "請輸入商品價格。",
              "The stock field is required.": "請輸入庫存數量。",
              "The condition field is required.": "請選擇商品狀況。",
              "The product code has already been taken.":
                "此商品代碼已被使用。",
              "The variants field is required.": "請設定商品規格或價格。",
            };

            const rawErrors = Object.values(e.response.data.errors).flat();
            const translatedErrors = rawErrors.map((err) => {
              // Simple exact match or partial match
              for (const [eng, chi] of Object.entries(errorMap)) {
                if (err.includes(eng) || err === eng) return chi;
              }
              return err; // Fallback to original
            });

            msg = translatedErrors.join("\n");
          }
        } else if (e.response && e.response.data && e.response.data.message) {
          msg = e.response.data.message;
        }

        if (isCodeError) {
          Swal.fire({
            title: "商品代碼重複 (Duplicate Code)",
            text: `商品代碼 "${this.product.product_code}" 已經被使用，請更換代碼或編輯現有商品。`,
            icon: "error",
            showCancelButton: true,
            confirmButtonText: "查看現有商品 (View Existing)",
            cancelButtonText: "關閉 (Close)",
          }).then(async (result) => {
            if (result.isConfirmed) {
              // Fetch existing product ID
              try {
                // Search by exact code if possible, or search and filter
                // Since we added product_code to search, we can use search param
                const res = await request.get("/api/admin/products", {
                  params: { search: this.product.product_code },
                });
                // The search is "like", so we need to find exact match
                const existing = res.data.data.find(
                  (p) => p.product_code === this.product.product_code
                );

                if (existing) {
                  this.$router.push(`/products/${existing.id}/edit`);
                } else {
                  Swal.fire(
                    "Error",
                    "Could not find the existing product automatically. Please find it in the list.",
                    "error"
                  );
                }
              } catch (err) {
                Swal.fire(
                  "Error",
                  "Failed to retrieve existing product.",
                  "error"
                );
              }
            }
          });
        } else {
          Swal.fire("Error", msg, "error");
        }
      } finally {
        this.isLoading = false;
      }
    },
    // Detailed Types Definition
    getDetailedTypes() {
      // Find brand name
      if (!this.product.brand_id) return {};

      const brand = this.brands.find((b) => b.id === this.product.brand_id);
      if (!brand) return {};

      // Normalize name for matching
      // e.g. "Golf Gods" might be "Golf Gods" or "GolfGods"
      // We check if any key in config is substring of brand name or vice versa
      const brandKey = Object.keys(brandProductOptions).find(
        (k) =>
          brand.name.toLowerCase().includes(k.toLowerCase()) ||
          k.toLowerCase().includes(brand.name.toLowerCase())
      );

      if (!brandKey) return {};

      const options = brandProductOptions[brandKey];
      // Convert list to keyed object for Vue loop
      // Key can be label slug
      const result = {};
      options.forEach((opt) => {
        const key = opt.label
          .replace(/\s+/g, "_")
          .toLowerCase()
          .replace(/[()]/g, "");
        result[key] = opt;
      });
      return result;
    },

    onDetailedTypeChange(newDetailType) {
      const types = this.getDetailedTypes();
      const config = types[newDetailType];
      if (config) {
        this.product.product_type = config.type;
        this.product.product_type_detail = newDetailType; // Ensure this field exists in model

        // Auto-populate attributes
        // Replace completely or merge? "Replace" is safer for "switching" types behavior requested.
        // Deep copy to avoid reference issues
        this.product.options = JSON.parse(JSON.stringify(config.options));
      }
    },
    toggleRoot(root, e) {
      const checked = e.target.checked;
      const ids = new Set(this.product.categories);
      const collect = (cat) => {
        if (checked) ids.add(cat.id);
        else ids.delete(cat.id);
        if (cat.children) cat.children.forEach(collect);
      };
      collect(root);
      this.product.categories = Array.from(ids);
    },
    addOptionType() {
      this.product.options.push({ name: "", values: [] });
    },
    removeOptionType(index) {
      this.product.options.splice(index, 1);
    },
    addOptionValue(optIndex, event) {
      const val = event.target.value.trim();
      if (!val) return;

      const opt = this.product.options[optIndex];
      if (!opt.values.includes(val)) {
        opt.values.push(val);
      }
      event.target.value = "";
    },
    removeOptionValue(optIndex, valIndex) {
      this.product.options[optIndex].values.splice(valIndex, 1);
    },
    removeVariant(index) {
      this.variants.splice(index, 1);
    },
    // Marketing Templates
    async openSaveTemplateModal() {
      if (
        !this.product.details.marketing_features ||
        this.product.details.marketing_features.length === 0
      ) {
        Swal.fire("Warning", "No marketing content to save.", "warning");
        return;
      }

      const { value: name } = await Swal.fire({
        title: "Save Marketing Template",
        input: "text",
        inputLabel: "Template Name",
        inputPlaceholder: "Enter a name for this template",
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) {
            return "You need to write something!";
          }
        },
      });

      if (name) {
        this.saveTemplate(name);
      }
    },
    async saveTemplate(name) {
      try {
        const content = {
          marketing_features: this.product.details.marketing_features,
          marketing_features_style: this.product.details
            .marketing_features_style,
          marketing_banner: this.product.details.marketing_banner,
        };

        await request.post("/api/admin/marketing-templates", {
          name,
          content,
        });

        Swal.fire("Success", "Template saved successfully!", "success");
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to save template.", "error");
      }
    },
    async openLoadTemplateModal() {
      try {
        const res = await request.get("/api/admin/marketing-templates");
        const templates = res.data.data;

        if (templates.length === 0) {
          Swal.fire("Info", "No templates available.", "info");
          return;
        }

        // Create clickable list HTML
        let html = '<div class="list-group text-start">';
        templates.forEach((t) => {
          const date = new Date(t.created_at).toLocaleDateString();
          html += `
                    <button class="list-group-item list-group-item-action d-flex justify-content-between align-items-center" onclick="window.selectMarketingTemplate(${t.id})">
                        <div>
                            <span class="fw-bold">${t.name}</span>
                            <br><small class="text-muted">${date}</small>
                        </div>
                        <i class="fas fa-chevron-right text-muted"></i>
                    </button>
                `;
        });
        html += "</div>";

        // Use global event handler helper
        window.selectMarketingTemplate = (id) => {
          const selected = templates.find((t) => t.id === id);
          if (selected) {
            this.loadTemplate(selected);
            Swal.close();
          }
        };

        Swal.fire({
          title: "Select Template",
          html: html,
          showConfirmButton: false,
          showCloseButton: true,
          width: "600px",
        });
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to fetch templates.", "error");
      }
    },
    loadTemplate(template) {
      try {
        // Apply content
        const content = template.content; // Automatically parsed as object by axios/backend cast
        this.product.details.marketing_features = JSON.parse(
          JSON.stringify(content.marketing_features || [])
        );
        this.product.details.marketing_features_style = JSON.parse(
          JSON.stringify(content.marketing_features_style || {})
        );
        this.product.details.marketing_banner = JSON.parse(
          JSON.stringify(content.marketing_banner || {})
        );

        Swal.fire("Loaded", `Template "${template.name}" applied.`, "success");
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Failed to apply template.", "error");
      }
    },
  },

  created() {
    this.fetchBrands();
    this.fetchCategories();
    this.fetchProduct();
  },
};
</script>

<style scoped>
.font-serif {
  font-family: "Noto Serif TC", serif;
}
.premium-input {
  border-radius: 0;
  border: 1px solid #dee2e6;
  padding: 0.75rem;
  font-size: 0.9rem;
}
.premium-input:focus {
  box-shadow: none;
  border-color: #1a1a1a;
}
.hover-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
.fade-in {
  animation: fadeIn 0.4s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.text-xxs {
  font-size: 0.65rem;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
